import { useDispatch, useSelector } from 'react-redux';
import { selectCompanyOffers } from '../../../../store/slices/InitialDataSlice';
import { useEffect } from 'react';
import moment from 'moment-timezone';
import { setOffers } from '../../../../store/slices/OfferSlice';
import { Offer } from '../../../../services/exports/Interfaces';
import { Moment } from 'moment';
import useValidateOfferProducts from '../useValidateOfferProducts';
import { OFFERS_PRODUCTS_KEYS_BY_KIND, OFFERS_WITH_PRODUCTS } from '../../consts';
import useGetCompanyMenusApi from '../../../../hooks/api/companies/useGetCompanyMenusApi';
import { OfferTypeEnum } from '../../../../services/exports/Constants';

const AUTO_REFRESH_INTERVAL_MS = 1000 * 60;
const SORT_ORDER_BY_TYPE: Readonly<Record<OfferTypeEnum, number>> = {
  [OfferTypeEnum.Bogo]: 1,
  [OfferTypeEnum.PercentageOff]: 2,
  [OfferTypeEnum.AmountOff]: 3,
  [OfferTypeEnum.FreeItem]: 4,
} as const;

const useWatchActiveOffers = () => {
  const companyOffers = useSelector(selectCompanyOffers);
  const dispatch = useDispatch();
  const { isLoading } = useGetCompanyMenusApi();
  const validateProduct = useValidateOfferProducts();
  const setActiveOffers = (offers: Offer[] = []) => {
    dispatch(
      setOffers({
        activeOffers: offers,
      }),
    );
  };
  const getTimeRunning = (now: Moment, time: string): Moment => {
    const [hour, minute] = time.split(':').map(Number);

    return now.clone().set({
      h: hour,
      m: minute,
      s: 0,
      ms: 0,
    });
  };
  const validateOfferAvailability = (offer: Offer): boolean => {
    // it's for backwards compatibility, old offers don't have a timezone, this can be removed later
    if (!offer.timezone) {
      return;
    }

    const now = moment().tz(offer.timezone);

    if (offer.ends_at && now.isSameOrAfter(offer.ends_at)) {
      return;
    }

    if (!offer.days_running.includes(now.isoWeekday())) {
      return;
    }

    if (!offer.is_happy_hour) {
      return true;
    }

    const timeRunningStartsAt = getTimeRunning(now, offer.time_running_starts_at);
    const timeRunningEndsAt = getTimeRunning(now, offer.time_running_ends_at);

    if (now.isBefore(timeRunningStartsAt)) {
      return;
    }

    if (!now.isBefore(timeRunningEndsAt)) {
      return false;
    }

    if (!OFFERS_WITH_PRODUCTS.includes(offer.kind)) {
      return true;
    }

    return OFFERS_PRODUCTS_KEYS_BY_KIND[offer.kind].every((key) => offer.details[key]?.some(validateProduct));
  };
  const filterUnavailableProducts = (offer: Offer): Offer => {
    if (!OFFERS_WITH_PRODUCTS.includes(offer.kind)) {
      return offer;
    }

    const clonedOffer = structuredClone(offer);

    for (const key of OFFERS_PRODUCTS_KEYS_BY_KIND[offer.kind]) {
      clonedOffer.details[key] = clonedOffer.details[key].filter(validateProduct);
    }

    return clonedOffer;
  };
  const filterOfferWithoutAvailableProducts = (offer: Offer): boolean => {
    if (!OFFERS_WITH_PRODUCTS.includes(offer.kind)) {
      return true;
    }

    return OFFERS_PRODUCTS_KEYS_BY_KIND[offer.kind].every((key) => offer.details[key]?.length);
  };
  const activeOffersHandler = (): void => {
    if (!companyOffers?.length) {
      setActiveOffers([]);
      return;
    }

    const validOffers = companyOffers
      .filter(validateOfferAvailability)
      .map(filterUnavailableProducts)
      .filter(filterOfferWithoutAvailableProducts);
    const result = [...validOffers].sort((a, b) => SORT_ORDER_BY_TYPE[a.type] - SORT_ORDER_BY_TYPE[b.type]);

    setActiveOffers(result);
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    activeOffersHandler();

    const offerTimer = setInterval(activeOffersHandler, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(offerTimer);
    };
  }, [companyOffers, isLoading, validateProduct]);
};

export default useWatchActiveOffers;
